import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * SnackbarContext - Global state management for snackbar notifications
 * Allows any component to trigger snackbar with refresh notification
 */
const SnackbarContext = createContext();

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

export const SnackbarProvider = ({ children }) => {
  const [snackbarState, setSnackbarState] = useState({
    isVisible: false,
    message: '',
    autoHide: true
  });

  // Show snackbar with optional custom message
  const showSnackbar = useCallback((message = "New changes were made...refresh", autoHide = true) => {
    setSnackbarState({
      isVisible: true,
      message,
      autoHide
    });

    // Auto-hide after 8 seconds if enabled
    if (autoHide) {
      setTimeout(() => {
        setSnackbarState(prev => ({ ...prev, isVisible: false }));
      }, 8000);
    }
  }, []);

  // Hide snackbar manually
  const hideSnackbar = useCallback(() => {
    setSnackbarState(prev => ({ ...prev, isVisible: false }));
  }, []);

  // Show snackbar for community post actions
  const showPostUpdateSnackbar = useCallback(() => {
    showSnackbar("Community post updated...refresh to see changes");
  }, [showSnackbar]);

  // Show snackbar for AI Assistant actions
  const showAIActionSnackbar = useCallback((actionType = "action") => {
    const messages = {
      class: "New class created...refresh to see it in your feed",
      event: "New event created...refresh to see it in your feed", 
      prayer: "Prayer time updated...refresh to see changes",
      action: "AI assistant made changes...refresh to see updates"
    };
    showSnackbar(messages[actionType] || messages.action);
  }, [showSnackbar]);

  const value = {
    ...snackbarState,
    showSnackbar,
    hideSnackbar,
    showPostUpdateSnackbar,
    showAIActionSnackbar
  };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
    </SnackbarContext.Provider>
  );
};